/**
 * Thin wrapper shim around `cmdk` (the shadcn `Command` primitive).
 *
 * Re-exports the named subcomponents so the FormCacheSelector imports them
 * from `lib/ui/command`. Lets tests mock a single module instead of cmdk's
 * internals, and decouples future swaps to a Kit-provided combobox.
 *
 * All exports are forwardRef wrappers that apply shadcn-style Tailwind classes
 * so the combobox renders with correct padding, hover/selected states, and
 * scroll containment out of the box — matching the standard shadcn spec.
 */

import * as React from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { Search } from 'lucide-react';

export const Command = CommandPrimitive;

export const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className = '', ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={['max-h-[300px] overflow-y-auto overflow-x-hidden', className].filter(Boolean).join(' ')}
    {...props}
  />
));
CommandList.displayName = 'CommandList';

export const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>(({ className = '', ...props }, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className={['py-6 text-center text-sm text-gray-500', className].filter(Boolean).join(' ')}
    {...props}
  />
));
CommandEmpty.displayName = 'CommandEmpty';

export const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className = '', ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={['overflow-hidden p-1 text-gray-900', className].filter(Boolean).join(' ')}
    {...props}
  />
));
CommandGroup.displayName = 'CommandGroup';

export const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className = '', ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={['relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-gray-100', className].filter(Boolean).join(' ')}
    {...props}
  />
));
CommandItem.displayName = 'CommandItem';

export const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className = '', ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={['-mx-1 h-px bg-gray-200', className].filter(Boolean).join(' ')}
    {...props}
  />
));
CommandSeparator.displayName = 'CommandSeparator';

export const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className = '', ...props }, ref) => (
  <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
    <Search className="mr-2 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
    <CommandPrimitive.Input
      ref={ref}
      className={['flex h-9 w-full bg-transparent py-2 text-sm outline-none placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-50', className].filter(Boolean).join(' ')}
      {...props}
    />
  </div>
));
CommandInput.displayName = 'CommandInput';
